import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Trash2 } from "lucide-react";
import { api, errMsg, fmtBytes, fmtDate } from "@/lib/api";
import { useFiles } from "@/context/FilesContext";
import { FileIcon } from "@/components/files/FileIcon";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function Trash() {
  const [data, setData] = useState(null);
  const [purge, setPurge] = useState(null);
  const { bump } = useFiles();
  const load = () => api.get("/trash").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); bump(); } catch (e) { toast.error(errMsg(e)); } };
  const items = data ? [...data.folders.map((f) => ({ ...f, kind: "folder" })), ...data.files.map((f) => ({ ...f, kind: "file" }))] : [];

  return (
    <div className="max-w-5xl mx-auto fade-up" data-testid="trash-page">
      <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Conservation</p>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">Corbeille</h1>
      <p className="text-sm text-slate-500 mt-1 mb-6">Les éléments sont supprimés définitivement après 30 jours.</p>
      {data && items.length === 0 && <div className="border border-dashed rounded-xl py-16 text-center text-sm text-slate-500" data-testid="trash-empty">La corbeille est vide</div>}
      <div className="bg-white border rounded-xl divide-y">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 px-4 py-3" data-testid={`trash-item-${it.id}`}>
            <FileIcon file={it} folder={it.kind === "folder"} />
            <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{it.name}</div>
              <div className="text-xs text-slate-500">{it.kind === "folder" ? "Dossier" : fmtBytes(it.size)} · supprimé le {fmtDate(it.deleted_at, true)}</div></div>
            <Button variant="outline" size="sm" onClick={() => act(() => api.post(`/trash/${it.kind}/${it.id}/restore`), "Restauré")} data-testid={`trash-restore-${it.id}`}>
              <RotateCcw className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Restaurer</span></Button>
            <Button variant="ghost" size="icon" className="text-red-600" aria-label="Supprimer définitivement" onClick={() => setPurge(it)} data-testid={`trash-purge-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <AlertDialog open={!!purge} onOpenChange={(o) => !o && setPurge(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Supprimer définitivement ?</AlertDialogTitle>
            <AlertDialogDescription>« {purge?.name} » ne pourra plus être restauré.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="trash-purge-confirm"
              onClick={() => act(() => api.delete(`/trash/${purge.kind}/${purge.id}`), "Supprimé définitivement")}>Supprimer</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
